import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Package, Train, Anchor, ArrowUpRight } from 'lucide-react';

const services = [
  {
    icon: Truck,
    title: 'Full Truckload',
    tag: 'FTL',
    description: 'Dedicated 53-ft trailers. Your freight, your truck. Direct transit, no terminals, no delays.',
    link: '/services/ftl',
    image: 'https://images.unsplash.com/photo-1760127831098-6a75c69ee79c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbmlnaHR8ZW58MHx8fHwxNzc4ODgzOTM3fDA&ixlib=rb-4.1.0&q=85',
    span: 'md:col-span-8 md:row-span-2',
    tall: true,
  },
  {
    icon: Package,
    title: 'Less Than Truckload',
    tag: 'LTL',
    description: 'Cost-effective partial loads. Pay only for the space you use.',
    link: '/services/ltl',
    image: 'https://images.unsplash.com/photo-1685119166946-d4050647b0e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBib3hlc3xlbnwwfHx8fDE3Nzg4ODM5Mzd8MA&ixlib=rb-4.1.0&q=85',
    span: 'md:col-span-4',
  },
  {
    icon: Train,
    title: 'Intermodal',
    tag: 'RAIL+TRUCK',
    description: 'Rail efficiency + truck flexibility. Save 10-30% on 500+ mile lanes.',
    link: '/services/intermodal',
    span: 'md:col-span-4',
  },
  {
    icon: Anchor,
    title: 'Drayage',
    tag: 'PORT',
    description: 'Port-to-door container moves. Avoid demurrage and detention fees.',
    link: '/services/drayage',
    image: 'https://images.unsplash.com/photo-1492168732976-2676c584c675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBmcmVpZ2h0JTIwdHJ1Y2slMjBmbGVldHxlbnwwfHx8fDE3Nzg4ODM5NDZ8MA&ixlib=rb-4.1.0&q=85',
    span: 'md:col-span-12',
  },
];

export const ServicesSection = () => (
  <section className="py-24 bg-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-14">
        <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em]">What We Move</span>
        <h2 className="text-3xl sm:text-4xl font-black text-white mt-3 tracking-tight">
          Comprehensive Freight Solutions
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className={s.span}
          >
            <Link
              to={s.link}
              className={`group relative block bg-zinc-900 border border-zinc-800 overflow-hidden transition-colors duration-300 hover:border-zinc-700 h-full ${s.tall ? 'min-h-[420px]' : 'min-h-[200px]'}`}
              data-testid={`service-bento-${s.tag.toLowerCase().replace(/[+\s]/g,'-')}`}
            >
              {s.image && (
                <div className="absolute inset-0">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
                </div>
              )}
              <div className="relative z-10 p-8 flex flex-col justify-end h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-600/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest border border-zinc-700 px-2 py-0.5">{s.tag}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  {s.title}
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-red-500 transition-colors" />
                </h3>
                <p className="text-zinc-500 text-sm max-w-md" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{s.description}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
